import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Save, ArrowLeft, Loader2 } from "lucide-react";

import API from "../apiBase";
const BLOCCHI = [1, 2, 3, 4];
const NOMI = {
  1: "Blocco 1 — gli inizi",
  2: "Blocco 2 — le uscite",
  3: "Blocco 3 — il mare e l'estate",
  4: "Blocco 4 — il viaggio e il finale",
};

export default function ModificaPage() {
  const [tappe, setTappe] = useState({});
  const [nomi, setNomi] = useState({});
  const [saving, setSaving] = useState(null);
  const [savedAt, setSavedAt] = useState({});

  useEffect(() => {
    document.title = "✨ Modifica la storia";
    BLOCCHI.forEach((n) => {
      fetch(`${API}/api/media/${n}`)
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .then((d) => {
          setNomi((m) => ({ ...m, [n]: d.nome || NOMI[n] }));
          setTappe((t) => ({
            ...t,
            [n]: d.items.map((it) => ({
              name: decodeURIComponent(it.src.split("/").pop()),
              type: it.type,
              src: it.src,
              poster: it.poster,
              caption: it.caption || "",
              date: it.date || "",
            })),
          }));
        })
        .catch(() => setTappe((t) => ({ ...t, [n]: [] })));
    });
  }, []);

  const update = (n, i, field, value) =>
    setTappe((t) => ({
      ...t,
      [n]: t[n].map((it, j) => (j === i ? { ...it, [field]: value } : it)),
    }));

  const move = (n, i, dir) =>
    setTappe((t) => {
      const arr = [...t[n]];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return t;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...t, [n]: arr };
    });

  const sposta = async (n, i, target) => {
    const it = (tappe[n] || [])[i];
    if (!it) return;
    try {
      const res = await fetch(`${API}/api/media-sposta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: it.name, da: n, a: target }),
      });
      const d = await res.json();
      if (d.ok) {
        setTappe((t) => ({
          ...t,
          [n]: t[n].filter((_, j) => j !== i),
          [target]: [...(t[target] || []), it],
        }));
        setSavedAt((s) => ({ ...s, [n]: undefined, [target]: undefined }));
      }
    } catch {
      /* rete assente: riprova */
    }
  };

  const save = async (n) => {
    setSaving(n);
    try {
      const res = await fetch(`${API}/api/media-salva`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tappa: n,
          nome: nomi[n] || "",
          righe: tappe[n].map(({ name, caption, date }) => ({ name, caption, date })),
        }),
      });
      const d = await res.json();
      if (d.ok) {
        setSavedAt((s) => ({
          ...s,
          [n]: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
        }));
      }
    } catch {
      /* rete assente: riprova */
    }
    setSaving(null);
  };

  return (
    <div className="mod-page" data-testid="modifica-page">
      <header className="mod-header">
        <a href="/" className="mod-back" data-testid="mod-back-link">
          <ArrowLeft size={15} /> torna alla pagina
        </a>
        <h1 className="mod-title">Sistema la storia</h1>
        <p className="mod-sub">
          Scrivi frasi e date sotto ogni ricordo, riordinali con le frecce, poi
          premi “Salva blocco”. Le modifiche compaiono subito nella carrellata.
        </p>
      </header>

      {BLOCCHI.map((n) => (
        <section className="mod-block" key={n} data-testid={`mod-block-${n}`}>
          <div className="mod-block-head">
            <div className="mod-block-title">
              <span className="mod-block-label">Blocco {n}</span>
              <input
                className="mod-nome"
                value={nomi[n] ?? ""}
                placeholder="nome del blocco"
                onChange={(e) => setNomi((m) => ({ ...m, [n]: e.target.value }))}
                data-testid={`mod-nome-${n}`}
              />
            </div>
            <button
              className="mod-save"
              onClick={() => save(n)}
              disabled={saving === n}
              data-testid={`mod-save-${n}`}
            >
              {saving === n ? (
                <Loader2 size={15} className="stelle-icon-spin" />
              ) : (
                <Save size={15} />
              )}
              {savedAt[n] ? `Salvato ✓ ${savedAt[n]}` : "Salva blocco"}
            </button>
          </div>

          {(tappe[n] || []).map((it, i) => (
            <div className="mod-row" key={it.name} data-testid={`mod-row-${n}-${i}`}>
              <span className="mod-pos">{String(i + 1).padStart(2, "0")}</span>
              {it.type === "video" ? (
                <video
                  className="mod-thumb"
                  src={encodeURI(it.src)}
                  poster={it.poster ? encodeURI(it.poster) : undefined}
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img className="mod-thumb" src={encodeURI(it.src)} alt={it.name} loading="lazy" />
              )}
              <div className="mod-fields">
                <span className="mod-name">{it.name}</span>
                <input
                  value={it.caption}
                  placeholder="frase / didascalia"
                  onChange={(e) => update(n, i, "caption", e.target.value)}
                  data-testid={`mod-caption-${n}-${i}`}
                />
                <input
                  value={it.date}
                  placeholder="data (es. 12 giugno 2024)"
                  onChange={(e) => update(n, i, "date", e.target.value)}
                  data-testid={`mod-date-${n}-${i}`}
                />
              </div>
              <div className="mod-arrows">
                <button
                  onClick={() => move(n, i, -1)}
                  disabled={i === 0}
                  aria-label="Sposta su"
                  data-testid={`mod-up-${n}-${i}`}
                >
                  <ArrowUp size={15} />
                </button>
                <button
                  onClick={() => move(n, i, 1)}
                  disabled={i === (tappe[n] || []).length - 1}
                  aria-label="Sposta giù"
                  data-testid={`mod-down-${n}-${i}`}
                >
                  <ArrowDown size={15} />
                </button>
                <select
                  className="mod-move"
                  value=""
                  onChange={(e) => e.target.value && sposta(n, i, Number(e.target.value))}
                  aria-label="Sposta in un altro blocco"
                  data-testid={`mod-move-${n}-${i}`}
                >
                  <option value="">Sposta…</option>
                  {BLOCCHI.filter((b) => b !== n).map((b) => (
                    <option key={b} value={b}>
                      → Blocco {b}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
